import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getExperiments,
  getPrompts,
  createExperiment,
  deleteExperiment,
  startExperiment,
  pauseExperiment,
  completeExperiment,
  ExperimentListItem,
  ExperimentCreate,
  ExperimentStatusType,
  getPrompt,
} from '../services/api'

const STATUS_OPTIONS: { value: ExperimentStatusType | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
]

const statusColors: Record<ExperimentStatusType, { bg: string; text: string; border: string }> = {
  draft: { bg: 'rgba(148, 163, 184, 0.2)', text: '#94a3b8', border: 'rgba(148, 163, 184, 0.3)' },
  running: { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
  paused: { bg: 'rgba(251, 191, 36, 0.2)', text: '#fbbf24', border: 'rgba(251, 191, 36, 0.3)' },
  completed: { bg: 'rgba(59, 130, 246, 0.2)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
}

export default function Experiments() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedPromptId, setSelectedPromptId] = useState<string>('')
  const [search, setSearch] = useState('')

  // Form state for creating experiment
  const [formData, setFormData] = useState<{
    name: string
    description: string
    prompt_id: string
    variants: { variant_name: string; prompt_version_id: string; traffic_percentage: number }[]
  }>({
    name: '',
    description: '',
    prompt_id: '',
    variants: [
      { variant_name: 'A', prompt_version_id: '', traffic_percentage: 50 },
      { variant_name: 'B', prompt_version_id: '', traffic_percentage: 50 },
    ],
  })

  const { data: experiments = [], isLoading } = useQuery({
    queryKey: ['experiments', statusFilter, selectedPromptId],
    queryFn: async () => {
      const response = await getExperiments({
        status: (statusFilter as ExperimentStatusType) || undefined,
        prompt_id: selectedPromptId || undefined,
      })
      return response.data
    },
  })

  const { data: prompts = [] } = useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const response = await getPrompts()
      return response.data
    },
  })

  // Get prompt detail with versions when form prompt_id changes
  const { data: selectedPromptDetail } = useQuery({
    queryKey: ['prompt', formData.prompt_id],
    queryFn: async () => {
      if (!formData.prompt_id) return null
      const response = await getPrompt(formData.prompt_id)
      return response.data
    },
    enabled: !!formData.prompt_id,
  })

  const createMutation = useMutation({
    mutationFn: (data: ExperimentCreate) => createExperiment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
      setShowCreateModal(false)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteExperiment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    },
  })

  const startMutation = useMutation({
    mutationFn: startExperiment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    },
  })

  const pauseMutation = useMutation({
    mutationFn: pauseExperiment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    },
  })

  const completeMutation = useMutation({
    mutationFn: completeExperiment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['experiments'] })
    },
  })

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      prompt_id: '',
      variants: [
        { variant_name: 'A', prompt_version_id: '', traffic_percentage: 50 },
        { variant_name: 'B', prompt_version_id: '', traffic_percentage: 50 },
      ],
    })
  }

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const filteredExperiments = experiments.filter(exp => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      exp.name.toLowerCase().includes(searchLower) ||
      (exp.description?.toLowerCase().includes(searchLower) ?? false) ||
      (exp.prompt_display_name?.toLowerCase().includes(searchLower) ?? false)
    )
  })

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(100, 180, 255, 0.1)',
    borderRadius: '16px',
  }

  const inputStyle = {
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(100, 180, 255, 0.15)',
    color: '#e2e8f0',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#f1f5f9' }}>
            A/B Experiments
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            Test different prompt versions and measure performance
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
            color: 'white',
            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
          }}
        >
          <i className="fa-solid fa-flask"></i>
          <span>New Experiment</span>
        </button>
      </div>

      {/* Filters */}
      <div className="p-4 rounded-xl" style={cardStyle}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search experiments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={inputStyle}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            value={selectedPromptId}
            onChange={(e) => setSelectedPromptId(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            <option value="">All Prompts</option>
            {prompts.map(p => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Experiments List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredExperiments.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={cardStyle}>
          <i className="fa-solid fa-flask text-4xl mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          <p style={{ color: '#94a3b8' }}>No experiments found</p>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
            Create an experiment to start A/B testing your prompts
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredExperiments.map((experiment) => (
            <ExperimentCard
              key={experiment.id}
              experiment={experiment}
              onStart={() => startMutation.mutate(experiment.id)}
              onPause={() => pauseMutation.mutate(experiment.id)}
              onComplete={() => completeMutation.mutate(experiment.id)}
              onDelete={() => {
                if (confirm(`Delete experiment "${experiment.name}"?`)) {
                  deleteMutation.mutate(experiment.id)
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6" style={cardStyle}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>Create New Experiment</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm() }} style={{ color: '#94a3b8' }}>
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Experiment Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Executive Summary - Concise vs Detailed"
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Description (optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What are you testing?"
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  Prompt to Test
                </label>
                <select
                  required
                  value={formData.prompt_id}
                  onChange={(e) => setFormData({ ...formData, prompt_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                >
                  <option value="">Select a prompt...</option>
                  {prompts.map(p => (
                    <option key={p.id} value={p.id}>{p.display_name}</option>
                  ))}
                </select>
              </div>

              {/* Variants */}
              {selectedPromptDetail && selectedPromptDetail.versions.length > 0 && (
                <div className="space-y-4">
                  <label className="block text-sm font-medium" style={{ color: '#94a3b8' }}>
                    Variants
                  </label>
                  {formData.variants.map((variant, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'rgba(30, 41, 59, 0.3)' }}>
                      <div className="w-12 text-center font-bold text-lg" style={{ color: '#60a5fa' }}>
                        {variant.variant_name}
                      </div>
                      <div className="flex-1">
                        <select
                          required
                          value={variant.prompt_version_id}
                          onChange={(e) => {
                            const newVariants = [...formData.variants]
                            newVariants[index].prompt_version_id = e.target.value
                            setFormData({ ...formData, variants: newVariants })
                          }}
                          className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                          style={inputStyle}
                        >
                          <option value="">Select version...</option>
                          {selectedPromptDetail.versions.map(v => (
                            <option key={v.id} value={v.id}>
                              Version {v.version_number} {v.is_current ? '(current)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="w-24">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          required
                          value={variant.traffic_percentage}
                          onChange={(e) => {
                            const newVariants = [...formData.variants]
                            newVariants[index].traffic_percentage = parseInt(e.target.value) || 0
                            setFormData({ ...formData, variants: newVariants })
                          }}
                          className="w-full px-3 py-2 rounded-lg text-sm text-center focus:outline-none"
                          style={inputStyle}
                        />
                      </div>
                      <span className="text-sm" style={{ color: '#94a3b8' }}>%</span>
                    </div>
                  ))}
                  <div className="text-sm" style={{ color: formData.variants.reduce((a, b) => a + b.traffic_percentage, 0) === 100 ? '#22c55e' : '#ef4444' }}>
                    Total: {formData.variants.reduce((a, b) => a + b.traffic_percentage, 0)}% (must equal 100%)
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm() }}
                  className="px-4 py-2 rounded-lg text-sm"
                  style={{ color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || formData.variants.reduce((a, b) => a + b.traffic_percentage, 0) !== 100}
                  className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Experiment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function ExperimentCard({
  experiment,
  onStart,
  onPause,
  onComplete,
  onDelete,
}: {
  experiment: ExperimentListItem
  onStart: () => void
  onPause: () => void
  onComplete: () => void
  onDelete: () => void
}) {
  const colors = statusColors[experiment.status]

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(100, 180, 255, 0.1)',
    borderRadius: '16px',
  }

  return (
    <div className="p-4" style={cardStyle}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-medium" style={{ color: '#f1f5f9' }}>
              {experiment.name}
            </h3>
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
              }}
            >
              {experiment.status.toUpperCase()}
            </span>
          </div>
          {experiment.description && (
            <p className="text-sm mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              {experiment.description}
            </p>
          )}
          <div className="flex items-center gap-4 text-sm" style={{ color: '#94a3b8' }}>
            <span>
              <i className="fa-solid fa-clipboard-list mr-1"></i>
              {experiment.prompt_display_name}
            </span>
            <span>
              <i className="fa-solid fa-code-branch mr-1"></i>
              {experiment.variant_count} variants
            </span>
            <span>
              <i className="fa-solid fa-chart-line mr-1"></i>
              {experiment.total_executions} executions
            </span>
            {experiment.start_date && (
              <span>
                <i className="fa-solid fa-calendar mr-1"></i>
                Started {new Date(experiment.start_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {experiment.status === 'draft' && (
            <button
              onClick={onStart}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
              style={{
                background: 'rgba(34, 197, 94, 0.2)',
                color: '#22c55e',
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            >
              <i className="fa-solid fa-play mr-1"></i>
              Start
            </button>
          )}
          {experiment.status === 'running' && (
            <>
              <button
                onClick={onPause}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'rgba(251, 191, 36, 0.2)',
                  color: '#fbbf24',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                }}
              >
                <i className="fa-solid fa-pause mr-1"></i>
                Pause
              </button>
              <button
                onClick={onComplete}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <i className="fa-solid fa-check mr-1"></i>
                Complete
              </button>
            </>
          )}
          {experiment.status === 'paused' && (
            <>
              <button
                onClick={onStart}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'rgba(34, 197, 94, 0.2)',
                  color: '#22c55e',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                }}
              >
                <i className="fa-solid fa-play mr-1"></i>
                Resume
              </button>
              <button
                onClick={onComplete}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
                style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }}
              >
                <i className="fa-solid fa-check mr-1"></i>
                Complete
              </button>
            </>
          )}
          {experiment.status !== 'running' && (
            <button
              onClick={onDelete}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <i className="fa-solid fa-trash"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
